// AXIS ZERO - Game Canvas Component
// Main game canvas with touch/click handling
// FIXED: Mobile Chrome viewport height using getBoundingClientRect and visualViewport API

import React, { useEffect, useRef } from 'react';

export const GameCanvas = ({ canvasRef, containerRef, onTap }) => {
  // Use ref for callback to avoid re-registering listeners
  const onTapRef = useRef(onTap);
  
  // Keep ref updated
  useEffect(() => {
    onTapRef.current = onTap;
  }, [onTap]);

  // Canvas resize handler using container bounds (not window.innerHeight)
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const handleResize = () => {
      // Use getBoundingClientRect for accurate visible size
      const rect = container.getBoundingClientRect();
        const dpr = 1; // TEST: disable devicePixelRatio
      
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    };

    // Initial resize
    handleResize();

    // Standard resize listener
    window.addEventListener('resize', handleResize);

    // visualViewport API for mobile Chrome address bar changes
    let visualViewportHandler = null;
    if (window.visualViewport) {
      visualViewportHandler = () => {
        handleResize();
      };
      window.visualViewport.addEventListener('resize', visualViewportHandler);
    }

    // Orientation change listener
    const handleOrientationChange = () => {
      setTimeout(handleResize, 100);
    };
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport && visualViewportHandler) {
        window.visualViewport.removeEventListener('resize', visualViewportHandler);
      }
    };
  }, [containerRef, canvasRef]);

  // Touch/click/keyboard event handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Handle touch events
    const handleTouchStart = (e) => {
      e.preventDefault();
      if (onTapRef.current) {
        onTapRef.current();
      }
    };

    // Handle mouse events (for desktop testing)
    const handleMouseDown = (e) => {
      e.preventDefault();
      if (onTapRef.current) {
        onTapRef.current();
      }
    };

    // Handle keyboard (spacebar)
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        if (onTapRef.current) {
          onTapRef.current();
        }
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef]); // Only depend on containerRef, not onTap

  return (
    <div
      ref={containerRef}
      data-testid="game-container"
      className="absolute inset-0 w-full h-full cursor-pointer touch-none select-none"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <canvas
        ref={canvasRef}
        data-testid="game-canvas"
        className="block w-full h-full"
      />
    </div>
  );
};

export default GameCanvas;
